import { isCancellableState } from "@sirdab/returns-core";
import type { LineState } from "@sirdab/returns-shared";

/**
 * How a per-line state PRESENTS on tracking. Nothing here invents copy: every word still
 * comes from the catalogue (`state.*`). This module only decides tone and which panels a
 * state is allowed to open.
 *
 * The distinction that matters:
 *
 *   - `rejected` is the only state that may open the rejection callout. A shopper who
 *     cancelled her own return used to be recorded as `rejected`, so her tracking page told
 *     her the merchant had refused her. Core now refuses a `rejectionReason` on a
 *     `cancelled` line at the schema level; `rejectionPanelVisible` is that same rule
 *     expressed in the UI, so no future field can smuggle blame back onto a calm exit.
 *   - `booking_failed` is IN FLIGHT, not a failure the shopper caused and not an ending —
 *     the carrier collection has not been booked yet and there is a retry path. It is toned
 *     as pending, never as danger, and never asks the shopper to act.
 *   - `cancelled` is a completed, blameless outcome. Closed, quiet, no callout.
 */
export type LineTone = "default" | "pending" | "closed" | "danger";

export function lineTone(state: LineState): LineTone {
  if (state === "rejected") return "danger";
  if (state === "cancelled" || state === "completed") return "closed";
  if (state === "booking_failed") return "pending";
  return "default";
}

/**
 * The shopper-facing stage a line sits in. The merchant's review is the shopper's whole
 * story until goods move, so `approved` and `awaiting_pickup` both read as "In review"
 * (the words come from the catalogue); `completed` is the merchant's neutral close-out.
 */
export type LineStage = "in_review" | "done";

export function lineStage(state: LineState): LineStage | null {
  if (state === "approved" || state === "awaiting_pickup") return "in_review";
  if (state === "completed") return "done";
  return null;
}

/**
 * The rejection callout renders for `rejected` and for nothing else. docs/04 makes a
 * rejection reason mandatory and rendered in full — that requirement belongs to `rejected`
 * alone, not to every state that happens to carry text.
 */
export function rejectionPanelVisible(state: LineState): boolean {
  return state === "rejected";
}

/**
 * Whether the shopper may still withdraw this line. Delegates to `@sirdab/returns-core` rather than
 * repeating the state list here: a second copy of the state machine in the UI drifts from
 * the first, which is exactly what already happened in the API layer.
 */
export function canCancelLine(state: LineState): boolean {
  return isCancellableState(state);
}
